import { CONSULTATION_STEPS, CONSULTATION_TYPE, MODULE_TYPE, OTHER_SERVICE, SERVICES_ORDER, STEP_TYPE } from '@abiglobalhealth/go-types'
import { useEffect, useMemo } from 'react'

import { BackButton } from '@/components/BackButton'
import { CancelConsultationButton } from '@/components/Consultation/components/CancelConsultationButton'
import { GeneralErrorsBanner } from '@/components/GeneralErrorsBanner'
import { useCheckBoxModal } from '@/hooks/useCheckBoxModal'
import { useConfig } from '@/hooks/useConfig'
import { useConsultation } from '@/hooks/useConsultation'
import { useConsultationEnded } from '@/hooks/useConsultationEnded'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useConsultationType } from '@/hooks/useConsultationType'
import { useJourney } from '@/hooks/useJourney'
import { useModule } from '@/hooks/useModule'
import useNavigator from '@/hooks/useNavigator'
import { useNavOnly } from '@/hooks/useNavOnly'
import { useStep } from '@/hooks/useStep'
import { useTagManager } from '@/hooks/useTagManager'
import { useUser } from '@/hooks/useUser'
import { getSlugTranslation, isRTL } from '@/locale/utils'
import { cn } from '@/utils/tailwind'

import { ConsultationIcon } from './ConsultationIcon'
import { Corner, POSITIONS } from './Corner'

export const TabBar = () => {
  const { consultationType, updateConsultationType, updatingConsultationType } = useConsultationType()
  const { currentConsultationStep } = useConsultationStep()
  const { config } = useConfig()
  const { currentModule } = useModule()
  const { currentStep } = useStep()
  const { consultation } = useConsultation()
  const { initialized } = useJourney()
  const consultationEnded = useConsultationEnded()
  const { onGoingConversation, navigator } = useNavigator()
  const { sendGTMEvent } = useTagManager()
  const { opened: checkBoxModalOpened } = useCheckBoxModal()
  const { user } = useUser()
  const { isNavOnly } = useNavOnly()

  const types = useMemo(() => {
    if (isNavOnly) return [consultationType]

    const availableServices = config?.consultation?.availableServices || []
    return availableServices.sort((a, z) => SERVICES_ORDER.indexOf(a) - SERVICES_ORDER.indexOf(z))
  }, [config?.consultation?.availableServices, isNavOnly, consultationType])

  const isLanding = useMemo(() => {
    if (!initialized) return true

    if (!consultationEnded) return false

    if (currentConsultationStep === CONSULTATION_STEPS.ANSWER) return false

    if (currentModule?.type === MODULE_TYPE.INDICATION_CATEGORIES) return true
    if (currentModule?.type === MODULE_TYPE.ASSESSMENT_SURVEY_CATEGORIES) {
      return currentConsultationStep !== CONSULTATION_STEPS.MENTAL_HEALTH_DISCLAIMER
    }

    if (currentStep?.type === STEP_TYPE.FULFILLMENT_CHOICE_SELECTION) return false

    if (currentModule?.type === MODULE_TYPE.CONSULTATION_LANDING) {
      if (currentStep?.type === STEP_TYPE.USER_FORM) return false

      if (consultationType === OTHER_SERVICE.HEALTH_NAVIGATOR) {
        if (!onGoingConversation || navigator.isServiceSelected) return !checkBoxModalOpened
        return false
      }

      return ![CONSULTATION_TYPE.PRESCRIPTION, CONSULTATION_TYPE.MENTAL].includes(currentModule?.consultationType as CONSULTATION_TYPE)
    }

    return false
  }, [
    initialized,
    currentModule,
    currentStep,
    consultation,
    currentConsultationStep,
    onGoingConversation,
    navigator,
    checkBoxModalOpened,
    consultationType
  ])

  const canCancel = useMemo(() => {
    if (currentModule?.type === MODULE_TYPE.INDICATION_SURVEY) return true

    if (consultationType === OTHER_SERVICE.HEALTH_NAVIGATOR && onGoingConversation && !navigator.healthNavigator.isFinished) return true

    switch (currentConsultationStep) {
      case CONSULTATION_STEPS.ANSWER:
      case CONSULTATION_STEPS.VIDEO_CALL_ENDED:
        return false

      case CONSULTATION_STEPS.ONGOING_VIDEO_CALL:
        // Ongoing, if not joined and not recalling can cancel
        return !consultation?.video?.patientJoinedAt && !consultation?.video?.isRecall

      case CONSULTATION_STEPS.MATCHING:
      case CONSULTATION_STEPS.MATCH:
      case CONSULTATION_STEPS.INDICATION_SURVEY:
      case CONSULTATION_STEPS.WAITING_FOR_CONSULTATION_PAYMENT:
      case CONSULTATION_STEPS.MENTAL_HEALTH_DISCLAIMER:
        return true

      default:
        return false
    }
  }, [consultation, currentConsultationStep, currentModule, onGoingConversation, navigator, consultationType])

  const onlyOneType = types.length === 1

  const showBackButton = useMemo(() => {
    if (currentStep?.type === STEP_TYPE.FULFILLMENT_CHOICE_SELECTION) return currentStep?.backButton

    if (consultationType === OTHER_SERVICE.HEALTH_NAVIGATOR) return checkBoxModalOpened

    return consultationEnded && currentConsultationStep !== CONSULTATION_STEPS.ANSWER
  }, [consultationEnded, currentConsultationStep, currentStep, consultationType, checkBoxModalOpened])

  const tabWidth = 100 / types.length
  const currentIndex = useMemo(() => types.indexOf(consultationType), [types, consultationType])

  const isNavigator = consultationType === OTHER_SERVICE.HEALTH_NAVIGATOR
  const isUserValidated = user?.routeConditions?.isUserValidated

  useEffect(() => {
    sendGTMEvent(`Openapp_${consultationType}`)
  }, [consultationType])

  const isNavOnlyOutsideNav = isNavOnly && consultationType !== OTHER_SERVICE.HEALTH_NAVIGATOR

  return (
    <div className={cn('sticky top-0 z-20 flex w-full flex-col', !isNavigator || (isNavigator && navigator.isServiceSelected) ? 'shadow-xs' : '')}>
      <header className={`bg-header bg-header-pattern flex flex-col bg-cover bg-no-repeat ${onlyOneType ? '' : 'gap-4'}`}>
        {isLanding ? (
          <>
            <div className={`flex h-15 w-full items-center ${onlyOneType ? 'px-6 pt-6 pb-3' : 'justify-around'} relative`}>
              {onlyOneType ? (
                <>
                  <ConsultationIcon consultationType={consultationType} customClassName="h-7 text-header-text" />
                  <h2 className={cn('text-header-text grow text-xl font-bold', isNavOnlyOutsideNav ? 'px-4 text-start' : 'text-center')}>
                    {getSlugTranslation(`general.consultation.${consultationType}.title`)}
                  </h2>
                  {isNavOnlyOutsideNav && <CancelConsultationButton />}
                </>
              ) : (
                <>
                  <span className="absolute inset-y-0 left-0 flex w-full">
                    <span
                      className={cn(
                        'bg-header-text ease-out-back h-full opacity-40 transition-[width] duration-300',
                        isRTL() ? 'rounded-bl-lg' : 'rounded-br-lg'
                      )}
                      style={{
                        width: `${currentIndex * tabWidth + 5}%`,
                        marginLeft: isRTL() ? '' : '-5%',
                        marginRight: isRTL() ? '-5%' : ''
                      }}
                    />
                    <span
                      className="relative h-full"
                      style={{
                        width: `${tabWidth}%`
                      }}
                    >
                      {currentIndex !== 0 && <Corner position={isRTL() ? POSITIONS.TOP_RIGHT : POSITIONS.TOP_LEFT} />}
                      {currentIndex !== types.length - 1 && <Corner position={isRTL() ? POSITIONS.TOP_LEFT : POSITIONS.TOP_RIGHT} />}
                    </span>
                    <span className={cn('bg-header-text h-full grow opacity-40', isRTL() ? 'rounded-br-lg' : 'rounded-bl-lg')} />
                  </span>

                  {types.map((type) => {
                    const isSelected = consultationType === type
                    return (
                      <button
                        key={type}
                        disabled={onlyOneType}
                        onClick={() => updateConsultationType(type)}
                        className={`relative flex items-center justify-center p-2.5 ${onlyOneType ? '' : 'w-full'}`}
                      >
                        <ConsultationIcon
                          consultationType={type}
                          containerClassName="h-auto"
                          customClassName="text-header-text w-auto hover:opacity-100"
                          outlined={!isSelected}
                        />
                      </button>
                    )
                  })}
                </>
              )}
            </div>

            {isNavigator && isUserValidated ? (
              navigator.isServiceSelected && (
                <h2
                  key={consultationType}
                  className="text-header-text px-6 pt-2 pb-5 text-center text-xl font-bold transition-opacity"
                  style={{
                    opacity: updatingConsultationType ? 0 : 1
                  }}
                >
                  {getSlugTranslation(`general.consultation.${consultationType}.title`)}
                </h2>
              )
            ) : (
              <div className="animate-fade-in flex flex-col gap-1 px-6 py-3" key={consultationType}>
                {!onlyOneType && (
                  <h2
                    className="text-header-text text-xl font-bold transition-opacity"
                    style={{
                      opacity: updatingConsultationType ? 0 : 1
                    }}
                  >
                    {getSlugTranslation(`general.consultation.${consultationType}.title`)}
                  </h2>
                )}

                <p
                  className="text-header-text min-h-[5rem] text-sm transition-opacity"
                  style={{
                    opacity: updatingConsultationType ? 0 : 1
                  }}
                >
                  {getSlugTranslation(`general.consultation.${consultationType}.description`)}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="animate-fade-in flex items-center p-4">
            {showBackButton ? <BackButton /> : <ConsultationIcon consultationType={consultationType} customClassName="text-header-text" />}

            <h2 className="text-header-text grow text-center text-lg font-bold">
              {getSlugTranslation(`general.consultation.${consultationType}.title`)}
            </h2>

            {canCancel && <CancelConsultationButton />}
          </div>
        )}
      </header>

      <GeneralErrorsBanner />
    </div>
  )
}
